import { HttpLink } from '@apollo/client';

import { expectedUserVar, sessionSlotSwitchInFlight } from './useSessionSlots';

/** One-shot guard: never queue more than one hard reload. */
let reloadingForUserSync = false;

function syncExpectedUserAndReload(resolvedUserID: string | undefined) {
	if (sessionSlotSwitchInFlight.current || reloadingForUserSync) {
		return;
	}

	reloadingForUserSync = true;
	expectedUserVar(resolvedUserID);
	window.location.reload();
}

export function useHttpLink() {
	async function fetch(info: RequestInfo | URL, init?: RequestInit): Promise<Response> {
		const expectedUserID = expectedUserVar();

		const response = await window.fetch(info, {
			...init,
			credentials: 'include',
			headers: {
				...((init?.headers ?? {}) as Record<string, string>),
				// Assert the user this tab expects to act as; the server rejects
				// the request when it doesn't match the cookie's active slot.
				// Bootstrap requests (before the var is seeded) go without it.
				...(expectedUserID ? { 'X-Treams-User': expectedUserID } : {}),
			},
		});

		// The server echoes the resolved user on every authenticated response.
		// A different user means another tab switched the active slot: sync and
		// hard-reload so this tab doesn't keep rendering as the wrong user.
		const resolvedUserID = response.headers.get('X-Treams-User');

		if (expectedUserID && resolvedUserID && resolvedUserID !== expectedUserID) {
			syncExpectedUserAndReload(resolvedUserID);

			return response;
		}

		// Only inspect errors for requests made with an authenticated
		// expectation; unauthenticated pages (e.g. /authentication itself)
		// must not redirect-loop on authorization errors.
		if (expectedUserID) {
			try {
				const json = await response.clone().json();
				const key: string | undefined = json['errors']?.[0]?.extensions?.error?.key;

				if (key === 'authorization.errors.sessionSlotMismatch') {
					syncExpectedUserAndReload(
						json['errors'][0].extensions.error.data?.resolvedUserID ?? undefined,
					);
				} else if (key?.startsWith('authorization.errors')) {
					// Don't re-enter /logout while a sign-out (or slot switch) is
					// already handling the broken session: Logout's own requests
					// can fail with authorization errors too, and re-assigning
					// /logout would restart the page mid-cleanup, forever.
					const willRedirect =
						!sessionSlotSwitchInFlight.current && window.location.pathname !== '/logout';
					if (willRedirect) {
						window.location.assign('/logout');
					}
				}
			} catch {
				// Non-JSON response (e.g. network-level failure); nothing to inspect.
			}
		}

		return response;
	}

	return new HttpLink({
		uri: import.meta.env.VITE_GRAPHQL_ENDPOINT ?? 'https://api.ontreams.com/graphql',
		fetch: (info, init) => fetch(info, init),
	});
}
